import { type ApiErrorCode, isApiError } from './api-error';

/**
 * 에러 코드 → 사용자 문구.
 * 화면은 서버 문구(`error.message`)를 그대로 보이지 않고 `getErrorMessage(error)` 를 쓴다.
 * 도메인 코드의 문구는 X3 가 코드를 등록할 때 여기에 함께 넣는다.
 */
export const ERROR_MESSAGE: Partial<Record<ApiErrorCode, string>> = {
  UNKNOWN_ERROR: '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.',
  BAD_REQUEST_INVALID_PARAMETERS: '입력한 내용을 다시 확인해 주세요.',
  UNAUTHORIZED: '로그인이 필요합니다.',
  UNAUTHORIZED_TOKEN_EXPIRED: '로그인 시간이 만료되었습니다. 다시 로그인해 주세요.',
  FORBIDDEN: '이 작업을 할 권한이 없습니다.',
  NOT_FOUND: '요청한 내용을 찾을 수 없습니다.',
  CONFLICT: '다른 변경과 충돌했습니다. 새로 고친 뒤 다시 시도해 주세요.',
  PAYLOAD_TOO_LARGE: '보내는 내용이 너무 큽니다.',
  TOO_MANY_REQUESTS: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
  SERVICE_UNAVAILABLE: '서비스 점검 중입니다.',
  NETWORK_ERROR: '서버에 연결할 수 없습니다. 네트워크 상태를 확인해 주세요.',
  TIMEOUT: '응답이 늦어지고 있습니다. 잠시 후 다시 시도해 주세요.',
  // 도메인 코드(구·신 비교 CHK-S09-07: 등록되지 않아 409 가 모두 "다른 변경과 충돌했습니다"로 보였다)
  CONFLICT_USERNAME_DUPLICATE: '이미 사용 중인 아이디입니다.',
  CONFLICT_PORTFOLIO_USERNAME_DUPLICATE: '이미 사용 중인 아이디입니다.',
  CONFLICT_INVESTMENT_IN_USE:
    '지분 변동이 있는 투자는 삭제할 수 없습니다. 지분 변동을 먼저 삭제해 주세요.',
  CONFLICT_INVESTMENT_EXITED: '이미 전량 회수된 투자입니다.',
  CONFLICT_INSUFFICIENT_SHARES: '그 날짜에 보유한 주식 수보다 많습니다.',
  CONFLICT_NOT_IMPAIRED: '그 날짜에 감액된 상태가 아닙니다.',
  CONFLICT_ROUND_IN_PROGRESS: '진행 중인 라운드가 이미 있습니다.',
  CONFLICT_ROUND_IN_USE: '투자가 연결된 라운드는 삭제할 수 없습니다.',
  CONFLICT_FUND_IN_USE: '관련 기록이 있는 펀드입니다.',
  CONFLICT_PORTFOLIO_FILE_NO_REPORT:
    '분기 리포트가 없어 파일을 올릴 수 없습니다. 분기 데이터를 먼저 추가해 주세요.',
  CONFLICT_MAIL_NO_TARGET: '발송할 대상이 없습니다.',
  CONFLICT_MAIL_NO_EMAIL: '로그인한 계정에 이메일이 없습니다.',
  CONFLICT_MAIL_HISTORY_STATUS: '지금 상태에서는 할 수 없는 작업입니다.',
  BAD_REQUEST_FILE_TYPE: '올릴 수 없는 파일 형식입니다.',
  BAD_REQUEST_CURRENT_PASSWORD: '현재 비밀번호가 일치하지 않습니다.',
  NOT_FOUND_PASSWORD_RESET_EMAIL: '등록되지 않은 이메일입니다.',
  BAD_REQUEST_MAIL_PURPOSE: '이 메일 종류에서 쓸 수 없는 목적입니다.',
  FORBIDDEN_IMPERSONATION_READ_ONLY: '대행 중에는 조회만 할 수 있습니다.',
  FORBIDDEN_NOT_MEMO_AUTHOR: '작성한 사람만 고칠 수 있습니다.',
  FORBIDDEN_NOT_HISTORY_AUTHOR: '작성한 사람만 고칠 수 있습니다.',
  FORBIDDEN_MEMBER_SELF_OR_ADMIN: '자기 자신이나 관리자는 바꿀 수 없습니다.',
  // 구·신 비교 CHK-S11-33 에서 빠진 것을 찾아 더함(구 서버 문구가 있으면 그대로)
  FORBIDDEN_PORTFOLIO_REPORT_PUBLISHED: 'LP에게 공개된 데이터는 D3멤버만 삭제가 가능합니다.',
  FORBIDDEN_IMPERSONATION_NOT_ALLOWED: '대행할 수 없는 계정입니다.',
  CONFLICT_PORTFOLIO_REPORT_DUPLICATE: '이미 있는 분기입니다.',
  CONFLICT_PORTFOLIO_REPORT_NOT_LATEST: '마지막 분기만 삭제할 수 있습니다.',
  CONFLICT_EXIT_REPORT_NOT_WRITABLE: '이 펀드는 회수 보고를 쓸 수 없습니다.',
  BAD_REQUEST_EQUITY_CHANGE_NOT_APPLICABLE: '이 투자에는 등록할 수 없는 지분 변동입니다.',
};

/** 코드에 문구가 없을 때 HTTP status 로 고른다. */
export const STATUS_MESSAGE: Readonly<Record<number, string>> = {
  400: '요청을 처리할 수 없습니다. 입력한 내용을 확인해 주세요.',
  401: '로그인이 필요합니다.',
  403: '이 작업을 할 권한이 없습니다.',
  404: '요청한 내용을 찾을 수 없습니다.',
  409: '다른 변경과 충돌했습니다. 새로 고친 뒤 다시 시도해 주세요.',
  413: '보내는 내용이 너무 큽니다.',
  429: '요청이 많습니다. 잠시 후 다시 시도해 주세요.',
  502: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  503: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  504: '응답이 늦어지고 있습니다. 잠시 후 다시 시도해 주세요.',
};

export const FALLBACK_ERROR_MESSAGE = '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';

export function getErrorMessage(error: unknown): string {
  if (!isApiError(error)) return FALLBACK_ERROR_MESSAGE;
  return ERROR_MESSAGE[error.code] ?? STATUS_MESSAGE[error.status] ?? FALLBACK_ERROR_MESSAGE;
}

export function getTraceId(error: unknown): string | undefined {
  return isApiError(error) ? error.traceId : undefined;
}
